import { useState } from "react";
import { useListContext, useNotify, useRefresh, useUpdateMany } from "ra-core";
import { Thermometer } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type { Contact, Temperature } from "../types";

// Sprint 9.5 — Bulk update temperature pour contacts sélectionnés.
// Popover avec 4 options + variante "perdu" en bas.

const OPTIONS: { value: Temperature; label: string; dot: string }[] = [
  { value: "chaud", label: "Chaud", dot: "bg-fabrik-red" },
  { value: "a_chauffer", label: "À chauffer", dot: "bg-fabrik-orange" },
  { value: "froid", label: "Froid", dot: "bg-fabrik-blue" },
  { value: "perdu", label: "Perdu", dot: "bg-fabrik-text-3" },
];

export function BulkTemperatureButton() {
  const { selectedIds = [], onUnselectItems } = useListContext<Contact>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [updateMany, { isPending }] = useUpdateMany();
  const [open, setOpen] = useState(false);

  const applyTemperature = (temperature: Temperature) => {
    if (selectedIds.length === 0) return;
    updateMany(
      "contacts",
      { ids: selectedIds, data: { temperature } },
      {
        onSuccess: () => {
          notify(`Température mise à jour (${selectedIds.length})`, {
            type: "success",
          });
          onUnselectItems?.();
          refresh();
          setOpen(false);
        },
        onError: (e: unknown) => {
          notify(
            `Échec mise à jour : ${e instanceof Error ? e.message : "erreur"}`,
            { type: "error" },
          );
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={isPending}
          className="gap-1.5 h-7"
        >
          <Thermometer className="size-3.5" />
          Température
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-44 p-1">
        {OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => applyTemperature(opt.value)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[13px] text-foreground hover:bg-fabrik-green-soft transition-colors"
          >
            <span className={cn("size-2 rounded-full", opt.dot)} />
            {opt.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
